'use client';

import { useEffect, useState } from 'react';
import { Pin, PictureInPicture2, Video } from 'lucide-react';
import {
  FloatingWindow,
  type FloatingWindowPosition,
} from '@/components/dashboard/FloatingWindow';
import type { HlsStreamState } from '@/components/dashboard/HlsVideo';
import { useDroneVideoStream } from './useDroneVideoStream';
import { CameraControlOverlay, type CameraControlInputs } from './CameraControlOverlay';
import { VideoStreamBody, playsPicture } from './VideoStreamBody';
import { VideoLatencyBadge } from './VideoLatencyBadge';
import type { VideoLatencyEstimate } from '@/lib/video-latency';
import { SINGLE_CAMERA } from './copReducers';
import { stageTakesBack } from './stageCamera';

/**
 * The picture, and nothing else — over the map, where the panel cannot reach.
 *
 * This IS where an aircraft's video lives. The details panel used to draw it
 * inline in FEED with this window as the pop-out; it draws no picture at all
 * now, and clicking an aircraft on the map opens one of these instead. FEED
 * keeps the numbers that describe the picture, and a key that opens or raises
 * the window that is showing it.
 *
 * So this window does open itself, for exactly one gesture — the map click —
 * and the `pinned` flag is what decides whether the next such click may close
 * it again. Every other way in is still a deliberate press, and every way out
 * is still the operator's: its own X, or the pin left off when they click the
 * next aircraft.
 *
 * Shares FloatingWindow with the dock camera windows, so the COP has one
 * floating-window behaviour rather than three that merely resemble each other:
 * fixed, dragged by its header, resized by its corner grip, closed from its
 * header, no collapse. That shell also holds pointer capture for the length of
 * both gestures, which is what stops a drag from clicking through onto the map.
 *
 * Camera control (issue #244) is [CameraControlOverlay], the same component the
 * manual-flight stage carries. It used to be on the inline FEED picture too;
 * control follows the picture, so when the picture left the panel the controls
 * went with it — a gimbal must never be commanded from a surface that is not
 * showing what it sees.
 *
 * It carries no stream metadata. Type, status, resolution and latency are the
 * FEED segment's rows; printing them here too would be the same four numbers in
 * two places, which is the rule this whole feature is built around.
 */

export interface CopVideoWindowProps extends CameraControlInputs {
  droneId: string;
  /** Which camera on the airframe. Undefined for an aircraft that registered a
   *  single unnamed feed. */
  cameraId?: string;
  /** Position in the open-window stack, for the cascade offset. */
  cascadeIndex?: number;
  /** Lifted so a window closed and reopened returns where the operator dragged
   *  it. Undefined until it has been moved — FloatingWindow places it. */
  position?: FloatingWindowPosition;
  onPositionChange?: (position: FloatingWindowPosition) => void;
  callsign: string;
  /** Hand this camera back to the STAGE — the manual-flight picture, or the
   *  split beside the map.
   *
   *  The only remaining destination: the details panel draws no picture any
   *  more, so a window whose aircraft is not on a stage has nowhere to be
   *  docked to and is given no dock verb at all. Withheld rather than shown and
   *  made a no-op, because "put this back" must never be indistinguishable from
   *  "make this vanish". Such a window keeps its X and its pin, and nothing
   *  else. */
  onDock?: () => void;
  onClose: () => void;
  /** aria-label for the X. Defaults to a generic one, but the COP names the
   *  aircraft: up to four of these are open at once and every one of them would
   *  otherwise announce the same three words. */
  closeLabel?: string;
  /** Bring this window to the front - see `FloatingWindow.onRaise`. */
  onRaise?: () => void;
  /** The stage is drawing this aircraft's picture (a manual session, or the
   *  split beside the map). With `poppedCameras`, it decides whether there is
   *  anywhere for `onDock` to land at all. */
  stageDrawsPicture?: boolean;
  poppedCameras?: string[];
  /** This window survives the next map click. Undefined for a window that was
   *  never at risk from one — a dock camera, or an aircraft window in a test
   *  that does not exercise the pin — and then no pin control is drawn. */
  pinned?: boolean;
  onTogglePin?: () => void;
  /** aria-label for the pin, named per aircraft for the same reason the X is:
   *  four of these can be on screen at once. */
  pinLabel?: string;
  /** This picture's own verdict, filed against its stream id, for the FEED
   *  health dot — which is read from a tab that draws no picture and so cannot
   *  observe one. `null` withdraws it when the window goes. */
  onStreamState?: (streamId: string, state: HlsStreamState | null) => void;
  /** This window was opened by the map click rather than by name, so it may
   *  remove itself when the registry answers that there is no camera here.
   *  See `nothingToShow`. A window the operator asked for never does. */
  transient?: boolean;
}

export function CopVideoWindow({
  droneId,
  cameraId,
  cascadeIndex = 0,
  position,
  onPositionChange,
  callsign,
  onDock,
  onClose,
  closeLabel = 'Close video window',
  onRaise,
  stageDrawsPicture = false,
  poppedCameras = [],
  pinned,
  onTogglePin,
  pinLabel,
  onStreamState,
  transient = false,
  ...cameraControl
}: CopVideoWindowProps) {
  const { streams, loading, error, refetch } = useDroneVideoStream(droneId);
  // Whether there is anywhere to dock to at all. The stage draws the first
  // camera not in a window, so leaving the window can hand this one straight
  // back to it — and if it cannot, no dock verb is drawn: the details panel
  // stopped being a destination when it stopped drawing a picture.
  const docksToStage =
    stageDrawsPicture && stageTakesBack(streams, poppedCameras, cameraId ?? SINGLE_CAMERA);
  /** What the player is doing, so the drag surface can refuse a tile that has
   *  stopped painting. `loading` counts as live — the player is still trying. */
  const [playerState, setPlayerState] = useState<HlsStreamState | null>(null);
  /** How far behind this picture is. The window is the surface an operator pops
   *  out to WATCH something happen - a tray approach, a descent - so it is the
   *  last place that should leave them guessing which transport won. */
  const [estimate, setEstimate] = useState<VideoLatencyEstimate | null>(null);
  // Pick THIS window's camera out of the aircraft's feeds. Falling back to the
  // first keeps a single-camera aircraft working unchanged.
  const stream = cameraId
    ? streams.find((s) => s.camera_id === cameraId) ?? null
    : streams[0] ?? null;
  const streamId = stream?.id ?? null;

  // This window is the only thing that can see whether the picture is actually
  // painting, and the details panel's health dot is read from a tab that shows
  // no picture at all — so the verdict is reported outward as well as kept here.
  //
  // Withdrawn when the window closes, or when it stops being about this stream.
  // A verdict nobody is observing any more must not go on standing: the dot
  // would keep saying "black" about a camera that has no player at all.
  const report = onStreamState;
  useEffect(() => {
    if (streamId === null || !report) return;
    return () => report(streamId, null);
  }, [streamId, report]);
  const reportState = (next: HlsStreamState) => {
    setPlayerState(next);
    if (streamId) report?.(streamId, next);
  };

  /**
   * A window nobody asked for, with nothing to show, takes itself away.
   *
   * The one exception to "a window only ever closes from its own X", and it is
   * narrow on purpose: `transient` is true only for the window the map click
   * opens by itself, and only once the registry has ANSWERED that this aircraft
   * has no camera. A fleet of MAVLink or RemoteID-only aircraft registers no
   * streams at all, so without this every click on every aircraft left a
   * 560 px "NO VIDEO FEED" box over the map to be dismissed by hand — while the
   * details panel, asked the same question, disables its key and says "no
   * stream is registered for this aircraft". Two surfaces, one fact, opposite
   * answers.
   *
   * `error` is excluded deliberately: a registry we could not REACH is not an
   * aircraft without a camera, and closing on it would hide a fault. That
   * window stays and says so.
   */
  const nothingToShow = transient && !loading && error === null && streams.length === 0;
  useEffect(() => {
    if (nothingToShow) onClose();
  }, [nothingToShow, onClose]);
  if (nothingToShow) return null;

  return (
    <FloatingWindow
      cascadeIndex={cascadeIndex}
      position={position}
      onPositionChange={onPositionChange}
      icon={<Video className="size-4 text-status-cyan-fg" aria-hidden />}
      title={`${stream?.label ?? stream?.camera_id ?? 'Drone'} Camera · ${callsign}`}
      onClose={onClose}
      onRaise={onRaise}
      closeLabel={closeLabel}
      // Named per camera, not a constant: an airframe with two feeds popped out
      // gives a screen reader two identical "Resize video window" buttons, which
      // is the exact thing this prop exists to prevent.
      resizeLabel={`Resize ${stream?.label ?? stream?.camera_id ?? 'drone'} camera window`}
      headerActions={
        <>
          {/* Dock is not the same verb as close: it moves the picture back to
              the stage, where the operator can still see it. Close ends it.
              Offering only X would make "put this back" indistinguishable from
              "I am done with this camera" — which is exactly why the verb is
              WITHHELD, rather than shown and made a no-op, when there is
              nowhere for this aircraft's picture to go. */}
          {onDock && docksToStage ? (
            <button
              type="button"
              onClick={onDock}
              className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:ring-offset-popover"
              aria-label="Return video to the stage"
              title="Return to the stage"
            >
              <PictureInPicture2 className="size-4" aria-hidden />
            </button>
          ) : null}
          {/* The pin, immediately left of the X, because the two answer the same
              question — how does this picture end. X ends it now; the pin
              decides whether the next aircraft the operator picks on the map
              ends it for them.

              ONE stable accessible name, with `aria-pressed` carrying the
              state. Two names plus aria-pressed is the documented toggle
              anti-pattern: "Unpin ALPHA video window, toggle button, pressed"
              states the same fact twice, once as a negation.

              The same `Pin` glyph in both states, never `PinOff`. A pin with a
              slash through it is the icon for "pinning unavailable", and it was
              being shown for the state in which pinning is the ONE thing this
              control does — a first-time operator reads a prohibition and does
              not press it. Pinned is carried by colour AND a filled chip
              behind the glyph, so the state does not rest on colour alone. */}
          {onTogglePin ? (
            <button
              type="button"
              onClick={onTogglePin}
              aria-pressed={pinned === true}
              // p-2, not p-1: a 32 px target rather than the bare 24 px WCAG
              // 2.2 minimum its neighbours sit at, and `mr-1` puts extra space
              // between it and the X. This is the control whose whole purpose is
              // "do not lose this picture", sitting next to the one that ends
              // it — a gloved thumb that misses must not hit Close.
              className={`mr-1 rounded p-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:ring-offset-popover ${
                pinned
                  ? 'bg-status-cyan/20 text-status-cyan-fg hover:bg-status-cyan/30'
                  : 'text-muted-foreground hover:bg-accent hover:text-foreground'
              }`}
              aria-label={pinLabel ?? 'Keep this video window open'}
              title={
                pinned
                  ? 'Pinned — stays open when you pick another aircraft on the map'
                  : 'Pin — keep this window when you pick another aircraft on the map'
              }
            >
              <Pin className="size-4" aria-hidden />
            </button>
          ) : null}
        </>
      }
    >
      {/* Test id rather than a class hook: the tests that drag across this box
          to prove no gimbal command is sent used to find it by `.aspect-video`,
          which stopped existing the moment the window became resizable. */}
      <div data-testid="video-window-body" className="relative h-full w-full bg-black">
        <VideoStreamBody
          callsign={callsign}
          stream={stream}
          loading={loading}
          error={error}
          onStreamState={reportState}
          onLatencyEstimate={setEstimate}
        />
        {/* `pictureLive` is the half of the F9 gate the stream row cannot
            answer — the player can give up on a URL that is still playable in
            principle, and its "Stream offline" tile is pointer-events-none, so
            a drag over dead black would otherwise still slew a live gimbal. */}
        <CameraControlOverlay
          stream={stream}
          pictureLive={playerState === null || playerState === 'playing'}
          {...cameraControl}
          // Not `compact`: that density is documented for the 288 px
          // picture-in-picture, and the control bar directly above this in the
          // same stack is full size. "PICTURE STALE - DO NOT USE" must not be
          // the smallest line in a window that opens at 560 px and that the
          // operator resizes UPWARD from there.
          footer={
            playsPicture(stream) && playerState !== 'error' ? (
              <VideoLatencyBadge estimate={estimate} stacked />
            ) : null
          }
        />
        {/* A way back from a failed registry read.

            This window is the primary video surface now, so a request that
            failed — an API blip, a token refresh — used to leave it printing
            the error and nothing else. The FEED panel has a RETRY, but it is a
            separate `useDroneVideoStream` instance, so pressing it there does
            not heal this window: the operator's only move was to close it and
            pick the aircraft again. Same word FEED uses, for the same act.

            Only over an error with no stream behind it. A failed refresh that
            left the last known stream standing keeps playing it — stale and
            labelled beats blank — and the picture is what the operator wants. */}
        {error !== null && stream === null && (
          <div className="absolute inset-x-0 bottom-0 flex justify-center p-3">
            <button
              type="button"
              onClick={refetch}
              className="rounded-md border border-status-blue/40 bg-status-blue/10 px-3 py-1.5 text-[10px] uppercase tracking-[0.1em] text-status-blue-fg hover:bg-status-blue/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              // Named, because up to four windows can be showing this at once.
              aria-label={`Retry reading the stream for ${callsign}`}
            >
              Retry
            </button>
          </div>
        )}
      </div>
    </FloatingWindow>
  );
}
